import { useState, useEffect } from 'react';
import { Node } from '@xyflow/react';
import {
  TextInput,
  TextArea,
  Button,
  Tag,
  Select,
  SelectItem,
  IconButton
} from '@carbon/react';
import { Save, Add, Close, Star, Connect, Locked, Unlocked } from '@carbon/icons-react';
import { ConceptNodeData } from './ConceptNode';
import { ProvenanceSection } from './ProvenanceSection';

interface NodeDetailsPanelProps {
  node: Node;
  onUpdateNode: (nodeId: string, data: Partial<ConceptNodeData>) => void;
  onExpandConcept?: (nodeId: string) => void;
  onLinkConcept?: (nodeId: string) => void;
  onLockNode?: (nodeId: string) => void;
  onUnlockNode?: (nodeId: string) => void;
  onClose?: () => void;
  isReadOnly?: boolean;
}

export const NodeDetailsPanel = ({ 
  node, 
  onUpdateNode, 
  onExpandConcept, 
  onLinkConcept,
  onLockNode,
  onUnlockNode,
  onClose,
  isReadOnly = false,
}: NodeDetailsPanelProps) => {
  const nodeData = node.data as ConceptNodeData;
  const [label, setLabel] = useState(nodeData.label);
  const [definition, setDefinition] = useState(nodeData.definition);
  const [properties, setProperties] = useState(nodeData.properties);
  const [newPropertyName, setNewPropertyName] = useState('');
  const [newPropertyType, setNewPropertyType] = useState('');

  useEffect(() => {
    const data = node.data as ConceptNodeData;
    setLabel(data.label);
    setDefinition(data.definition);
    setProperties(data.properties);
  }, [node.id, node.data]);

  const handleSave = () => {
    onUpdateNode(node.id, {
      label,
      definition,
      properties,
    });
  };

  const addProperty = () => {
    if (newPropertyName.trim()) {
      const newProp = newPropertyType.trim() 
        ? { name: newPropertyName.trim(), type: newPropertyType.trim() }
        : newPropertyName.trim();
      setProperties([...properties, newProp]);
      setNewPropertyName('');
      setNewPropertyType('');
    }
  };

  const removeProperty = (index: number) => {
    setProperties(properties.filter((_, i) => i !== index));
  };

  const hasChanges = label !== nodeData.label || 
    definition !== nodeData.definition || 
    JSON.stringify(properties) !== JSON.stringify(nodeData.properties);

  return (
    <div className="node-details-panel">
      {/* Header */}
      <div className="node-details-panel__header">
        <div className="node-details-panel__header-left">
          <div className="node-details-panel__node-indicator" />
          <span className="node-details-panel__title">Inspector</span>
        </div>
        {onClose && (
          <IconButton
            kind="ghost"
            size="sm"
            label="Close inspector"
            align="bottom-right"
            onClick={onClose}
            className="node-details-panel__close"
          >
            <Close size={16} />
          </IconButton>
        )}
      </div>

      {/* Provenance — read-only origin/author info, always visible. */}
      <ProvenanceSection
        provenance={nodeData.provenance}
        createdByName={nodeData.createdByName}
        updatedByName={nodeData.updatedByName}
        correctedAt={nodeData.correctedAt}
        testId="node-provenance"
      />

      {/* Quick Actions Bar — hidden in read-only mode */}
      {!isReadOnly && (
        <div className="node-details-panel__quick-actions">
          <button
            className="node-details-panel__quick-action"
            onClick={() => onExpandConcept?.(node.id)}
            title="Expand with AI"
          >
            <Star size={14} />
            <span>Expand</span>
          </button>
          <button
            className="node-details-panel__quick-action"
            onClick={() => onLinkConcept?.(node.id)}
            title="Link to other concepts"
          >
            <Connect size={14} />
            <span>Link</span>
          </button>
          <button
            className="node-details-panel__quick-action"
            onClick={() => nodeData.locked ? onUnlockNode?.(node.id) : onLockNode?.(node.id)}
            title={nodeData.locked ? 'Unlock position' : 'Lock position'}
          >
            {nodeData.locked ? <Unlocked size={14} /> : <Locked size={14} />}
            <span>{nodeData.locked ? 'Unlock' : 'Lock'}</span>
          </button>
        </div>
      )}

      {/* Form Content */}
      <div className="node-details-panel__form">
        {/* Label Field */}
        <div className="node-details-panel__field">
          <TextInput
            id="node-label"
            labelText="Label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Concept name..."
            size="sm"
            readOnly={isReadOnly}
          />
        </div>

        {/* Definition Field */}
        <div className="node-details-panel__field node-details-panel__field--definition">
          <TextArea
            id="node-definition"
            labelText="Definition"
            value={definition}
            onChange={(e) => setDefinition(e.target.value)}
            placeholder="Describe this concept..."
            rows={6}
            readOnly={isReadOnly}
          />
        </div>

        {/* Properties Section */}
        <div className="node-details-panel__field">
          <div className="node-details-panel__section-header">
            <span className="node-details-panel__section-label">Properties</span>
            <span className="node-details-panel__section-count">{properties.length}</span>
          </div>
          
          {/* Property Tags */}
          {properties.length > 0 && (
            <div className="node-details-panel__props-list">
              {properties.map((property, index) => {
                const propName = typeof property === 'string' ? property : property.name;
                const propType = typeof property === 'string' ? null : property.type;
                
                return (
                  <Tag
                    key={index}
                    type="cool-gray"
                    size="sm"
                    filter={!isReadOnly}
                    onClose={isReadOnly ? undefined : () => removeProperty(index)}
                    title={propType ? `${propName}: ${propType}` : propName}
                  >
                    {propName}
                    {propType && <span className="node-details-panel__prop-type">:{propType}</span>}
                  </Tag>
                );
              })}
            </div>
          )}

          {/* Add Property Row — hidden in read-only mode */}
          {!isReadOnly && (
            <div className="node-details-panel__add-prop">
              <TextInput
                id="new-prop-name"
                labelText=""
                hideLabel
                value={newPropertyName}
                onChange={(e) => setNewPropertyName(e.target.value)}
                placeholder="New property..."
                size="sm"
                onKeyDown={(e) => e.key === 'Enter' && addProperty()}
              />
              <Select
                id="new-prop-type"
                labelText=""
                hideLabel
                value={newPropertyType}
                onChange={(e) => setNewPropertyType(e.target.value)}
                size="sm"
              >
                <SelectItem value="" text="Type" />
                <SelectItem value="str" text="str" />
                <SelectItem value="int" text="int" />
                <SelectItem value="float" text="float" />
                <SelectItem value="bool" text="bool" />
                <SelectItem value="list" text="list" />
                <SelectItem value="dict" text="dict" />
                <SelectItem value="datetime" text="date" />
              </Select>
              <IconButton
                kind="ghost"
                size="sm"
                label="Add property"
                align="left"
                onClick={addProperty}
                disabled={!newPropertyName.trim()}
              >
                <Add size={16} />
              </IconButton>
            </div>
          )}
        </div>
      </div>

      {/* Footer with Save — hidden in read-only mode */}
      {!isReadOnly && (
        <div className="node-details-panel__footer">
          <Button
            kind="primary"
            size="sm"
            renderIcon={Save}
            onClick={handleSave}
            disabled={!hasChanges}
            className="node-details-panel__save-btn"
          >
            {hasChanges ? 'Save Changes' : 'Saved'}
          </Button>
        </div>
      )}
    </div>
  );
};

// Made with Bob
